import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaAdjust,
  FaCamera,
  FaInfoCircle,
  FaSlidersH,
  FaSun,
} from "react-icons/fa";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { isMobileAkanthasAccount } from "./mobileModuleUtils";
import InstallationMetricSnapshot from "./installation/InstallationMetricSnapshot";
import InstallationStateSupportSection from "./installation/InstallationStateSupportSection";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import InstallationBrightnessContrastControls from "./installation/InstallationBrightnessContrastControls";
import {
  BRIGHTNESS_RECOMMENDED_MODE,
  getBrightnessModeLabel,
  getBrightnessStatusKey,
  getBrightnessVisual,
} from "./installation/installationBrightnessDisplayUtils";
import { InstallationPickerOption } from "./installation/InstallationPickerSheet";
import {
  areBrightnessStatesEqual,
  BRIGHTNESS_MODES,
  buildCoefficientList,
  formatBrightnessSummary,
  isContrastSelectionValid,
  parseBrightnessModeFromConfig,
} from "./installation/installationBrightnessUtils";
import {
  INSTALLATION_DETAIL_STALE_KEY,
  INSTALLATION_LIST_STALE_KEY,
} from "./installation/installationConfigUtils";
import { saveBrightnessConfig } from "./installation/installationApi";
import { useInstallationCamera } from "./installation/useInstallationCamera";

const AppInstallationBrightness = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { loading, configData, setConfigData, api, token, reload } =
    useInstallationCamera(id);

  useEffect(() => {
    if (!isMobileAkanthasAccount()) {
      history.replace(`/app/installationdetails/${id}`);
    }
  }, [history, id]);

  const [savedState, setSavedState] = useState(() =>
    parseBrightnessModeFromConfig(configData),
  );
  const [mode, setMode] = useState(savedState.mode);
  const [shadowLevel, setShadowLevel] = useState(savedState.shadowLevel);
  const [highlightLevel, setHighlightLevel] = useState(
    savedState.highlightLevel,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = parseBrightnessModeFromConfig(configData);
    setSavedState(next);
    setMode(next.mode);
    setShadowLevel(next.shadowLevel);
    setHighlightLevel(next.highlightLevel);
  }, [configData]);

  const draftState = useMemo(
    () => ({
      mode,
      shadowLevel,
      highlightLevel,
      coefficients:
        mode === BRIGHTNESS_MODES.CONTRAST
          ? buildCoefficientList(shadowLevel, highlightLevel)
          : [1],
    }),
    [mode, shadowLevel, highlightLevel],
  );

  const isDirty = !areBrightnessStatesEqual(draftState, savedState);

  const canSave =
    isDirty &&
    (mode !== BRIGHTNESS_MODES.CONTRAST ||
      isContrastSelectionValid(shadowLevel, highlightLevel));

  const dismiss = () => {
    setMode(savedState.mode);
    setShadowLevel(savedState.shadowLevel);
    setHighlightLevel(savedState.highlightLevel);
    history.goBack();
  };

  const handleSave = useCallback(async () => {
    if (!canSave || saving) {
      if (
        mode === BRIGHTNESS_MODES.CONTRAST &&
        !isContrastSelectionValid(shadowLevel, highlightLevel)
      ) {
        toast.error(t("installation_brightness_contrast_validation"));
      }
      return;
    }

    setSaving(true);
    try {
      const { output } = await saveBrightnessConfig(
        api,
        token,
        id,
        configData,
        draftState,
      );
      const nextSaved = parseBrightnessModeFromConfig(output);
      setConfigData(output);
      setSavedState(nextSaved);
      sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
      sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
      toast.success(t("installation_saved"));
      await reload({ silent: true });
      history.goBack();
    } catch {
      toast.error(t("installation_save_failed"));
    } finally {
      setSaving(false);
    }
  }, [
    api,
    token,
    id,
    configData,
    draftState,
    canSave,
    saving,
    mode,
    shadowLevel,
    highlightLevel,
    setConfigData,
    t,
    reload,
    history,
  ]);

  const modeLabel = getBrightnessModeLabel(t, mode);
  const summaryText = formatBrightnessSummary(t, draftState);
  const visual = getBrightnessVisual(mode);
  const recommendedModeLabel = getBrightnessModeLabel(
    t,
    BRIGHTNESS_RECOMMENDED_MODE,
  );

  const guideItems = [
    {
      icon: FaInfoCircle,
      titleKey: "installation_brightness_about_title",
      bodyKey: "installation_brightness_about_body",
    },
    {
      icon: FaSun,
      titleKey: "installation_brightness_when_standard_title",
      bodyKey: "installation_brightness_when_standard_body",
    },
    {
      icon: FaAdjust,
      titleKey: "installation_brightness_when_hdr_title",
      bodyKey: "installation_brightness_when_hdr_body",
    },
    {
      icon: FaSlidersH,
      titleKey: "installation_brightness_when_contrast_title",
      bodyKey: "installation_brightness_when_contrast_body",
    },
    {
      icon: FaCamera,
      titleKey: "installation_brightness_test_title",
      bodyKey: "installation_brightness_test_body",
    },
  ];

  const headerAction = canSave ? (
    <button
      type="button"
      className="installation-subpage-nav-button installation-subpage-nav-button-save"
      onClick={() => void handleSave()}
      disabled={saving}
    >
      {saving ? t("installation_saving") : t("installation_save")}
    </button>
  ) : null;

  return (
    <InstallationSubpageLayout
      pageTitle={t("installation_brightness_page_title")}
      onBack={dismiss}
      setCookies={setCookies}
      saveUser={saveUser}
      headerAction={headerAction}
    >
      {loading && !configData && (
        <div className="installation-detail-state">
          <div className="installation-detail-spinner" aria-hidden />
          <p>{t("installation_loading")}</p>
        </div>
      )}

      {!loading && (
        <>
          <InstallationMetricSnapshot
            icon={FaSun}
            iconVariant="brightness"
            eyebrow={t("installation_brightness_current_title")}
            value={modeLabel}
            subtitle={summaryText}
            hint={t(getBrightnessStatusKey(mode))}
            tone={visual.tone}
          />

          <section
            className={`installation-battery-target installation-battery-target-${visual.tone}`}
          >
            <div className="installation-battery-target-header">
              <span className="installation-battery-target-label">
                {t("installation_brightness_threshold_title")}
              </span>
              <span className="installation-battery-target-value">
                {recommendedModeLabel}
              </span>
            </div>
            <div className="installation-battery-target-track" aria-hidden>
              <div
                className="installation-battery-target-fill"
                style={{ width: `${visual.fillPercent}%` }}
              />
              <span
                className="installation-battery-target-marker"
                style={{ left: `${visual.markerPercent}%` }}
              />
            </div>
            <p className="installation-battery-target-hint">
              {t("installation_brightness_threshold_body")}
            </p>
          </section>

          <section className="installation-subpage-card installation-brightness-settings-card">
            <h2 className="installation-subpage-card-title">
              {t("installation_brightness_settings_heading")}
            </h2>
            <InstallationPickerOption
              label={t("installation_brightness_mode_standard")}
              description={t("installation_brightness_mode_standard_desc")}
              selected={mode === BRIGHTNESS_MODES.STANDARD}
              onSelect={() => setMode(BRIGHTNESS_MODES.STANDARD)}
            />
            <InstallationPickerOption
              label={t("installation_brightness_mode_hdr_auto")}
              description={t("installation_brightness_mode_hdr_auto_desc")}
              selected={mode === BRIGHTNESS_MODES.HDR_AUTO}
              onSelect={() => setMode(BRIGHTNESS_MODES.HDR_AUTO)}
            />
            <InstallationPickerOption
              label={t("installation_brightness_mode_contrast")}
              description={t("installation_brightness_mode_contrast_desc")}
              selected={mode === BRIGHTNESS_MODES.CONTRAST}
              onSelect={() => setMode(BRIGHTNESS_MODES.CONTRAST)}
            />
          </section>

          {mode === BRIGHTNESS_MODES.CONTRAST && (
            <section className="installation-subpage-card installation-brightness-contrast-card">
              <InstallationBrightnessContrastControls
                shadowLevel={shadowLevel}
                highlightLevel={highlightLevel}
                onShadowLevelChange={setShadowLevel}
                onHighlightLevelChange={setHighlightLevel}
                disabled={saving}
              />
            </section>
          )}

          <section className="installation-battery-guide">
            {guideItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <article
                  key={item.titleKey}
                  className={
                    index > 0
                      ? "installation-battery-guide-item installation-battery-guide-item-divided"
                      : "installation-battery-guide-item"
                  }
                >
                  <span className="installation-battery-guide-icon" aria-hidden>
                    <Icon />
                  </span>
                  <div className="installation-battery-guide-body">
                    <h2 className="installation-battery-guide-title">
                      {t(item.titleKey)}
                    </h2>
                    <p className="installation-battery-guide-text">
                      {t(item.bodyKey)}
                    </p>
                  </div>
                </article>
              );
            })}
          </section>

          <InstallationStateSupportSection
            cameraId={id}
            emailSubjectKey="installation_brightness_support_email_subject"
          />
        </>
      )}
    </InstallationSubpageLayout>
  );
};

export default AppInstallationBrightness;
